import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  CURRENCY_PLACEHOLDER_LABEL,
  CURRENCY_UNSELECTED,
  DEFAULT_CURRENCY,
  filterCurrencies,
  formatCurrencyLabel,
  getCurrency,
} from "@/lib/currencies";

const COMMON_CODES = ["NGN", "GHS", "KES", "ZAR", "USD", "GBP", "EUR"];

function highlightMatch(text, query) {
  if (!query.trim()) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-[var(--color-gold-light)] text-[var(--color-text-primary)] rounded px-0.5">
        {text.slice(idx, idx + query.length)}
      </mark>
      {text.slice(idx + query.length)}
    </>
  );
}

/**
 * Searchable currency picker — tap field opens bottom sheet with search.
 */
export default function CurrencySelect({
  value,
  onChange,
  placeholder = CURRENCY_PLACEHOLDER_LABEL,
  testId = "currency-select",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const searchRef = useRef(null);
  const selected = value && value !== CURRENCY_UNSELECTED ? getCurrency(value) : null;
  const filtered = useMemo(() => filterCurrencies(query), [query]);

  const common = useMemo(
    () => COMMON_CODES.map((c) => getCurrency(c)).filter(Boolean),
    []
  );

  const rest = useMemo(() => {
    const commonSet = new Set(COMMON_CODES);
    return filtered.filter((c) => !commonSet.has(c.code) || query.trim());
  }, [filtered, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setTimeout(() => searchRef.current?.focus(), 100);
    }
  }, [open]);

  const pick = (code) => {
    onChange(code);
    setOpen(false);
    setQuery("");
  };

  const display =
    selected
      ? `${selected.flag} ${selected.label} (${selected.symbol})`
      : value === DEFAULT_CURRENCY
        ? formatCurrencyLabel(getCurrency(DEFAULT_CURRENCY))
        : placeholder;

  return (
    <>
      <button
        type="button"
        className="w-full flex items-center justify-between bg-[var(--color-surface)] border-[1.5px] border-[var(--color-border)] rounded-[var(--radius-lg)] px-4 py-3.5 text-[var(--text-base)] text-left kipa-btn-press"
        onClick={() => setOpen(true)}
        data-testid={testId}
      >
        <span className={selected || value === DEFAULT_CURRENCY ? "text-[var(--color-text-primary)] font-semibold" : "text-[var(--color-text-placeholder)]"}>
          {display}
        </span>
        <ChevronDown size={18} className="text-[var(--color-text-muted)] flex-shrink-0" />
      </button>

      {open && (
        <div className="kipa-sheet-overlay" onClick={() => setOpen(false)}>
          <div className="kipa-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 rounded-full bg-[var(--color-border)] mx-auto mb-4" />
            <div className="text-[var(--text-md)] font-extrabold text-[var(--color-text-primary)] mb-3">
              Choose currency
            </div>
            <input
              ref={searchRef}
              type="search"
              className="s-inp mb-3"
              placeholder="Search currency…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid={`${testId}-search`}
              autoComplete="off"
            />
            <ul
              className="max-h-[50vh] overflow-y-auto -mx-1"
              role="listbox"
              data-testid={`${testId}-list`}
            >
              {!query.trim() && (
                <>
                  {common.map((c) => (
                    <CurrencyRow
                      key={c.code}
                      c={c}
                      active={c.code === value}
                      query={query}
                      onPick={pick}
                      testId={testId}
                    />
                  ))}
                  <li className="px-3 py-2 text-[var(--text-xs)] font-bold uppercase tracking-wider text-[var(--color-text-muted)] border-t border-[var(--color-border)] mt-1">
                    All currencies
                  </li>
                </>
              )}
              {(query.trim() ? filtered : rest).map((c) => (
                <CurrencyRow
                  key={c.code}
                  c={c}
                  active={c.code === value}
                  query={query}
                  onPick={pick}
                  testId={testId}
                />
              ))}
              {filtered.length === 0 && (
                <li className="px-4 py-3 text-[var(--text-sm)] text-[var(--color-text-muted)]">
                  No currency found
                </li>
              )}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

function CurrencyRow({ c, active, query, onPick, testId }) {
  const label = formatCurrencyLabel(c);
  return (
    <li>
      <button
        type="button"
        role="option"
        aria-selected={active}
        onClick={() => onPick(c.code)}
        data-testid={`${testId}-option-${c.code}`}
        className={`w-full text-left px-3 py-2.5 text-[var(--text-sm)] rounded-[var(--radius-md)] transition-colors kipa-btn-press ${
          active
            ? "bg-[var(--color-primary-light)] text-[var(--color-primary)] font-bold"
            : "text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)]"
        }`}
      >
        {highlightMatch(label, query)}
        <span className="text-[var(--text-xs)] text-[var(--color-text-muted)] ml-1">· {c.code}</span>
      </button>
    </li>
  );
}
